import { Check, Copy } from "lucide-react";
import { type ComponentProps, memo, type ReactNode, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import "./hljs.css";

function nodeText(n: ReactNode): string {
  if (typeof n === "string") return n;
  if (Array.isArray(n)) return n.map(nodeText).join("");
  if (n && typeof n === "object" && "props" in n)
    return nodeText((n as { props: { children?: ReactNode } }).props.children);
  return "";
}

function CodeBlock({ children }: { children?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const lang = /language-([\w-]+)/.exec(
    ((children as { props?: { className?: string } })?.props?.className as string) ?? "",
  )?.[1];
  return (
    <div
      dir="ltr"
      className="group relative my-3 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface-2)]"
    >
      <div className="flex items-center justify-between border-b border-[var(--line)] px-3 py-1.5 text-[11px] text-[var(--fg-faint)]">
        <span className="font-mono uppercase tracking-wide">{lang ?? "text"}</span>
        <button
          type="button"
          className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-[var(--surface-3)] hover:text-[var(--fg)]"
          onClick={() => {
            navigator.clipboard?.writeText(nodeText(children).replace(/\n$/, ""));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 text-[0.85em] leading-relaxed">{children}</pre>
    </div>
  );
}

// `dir="auto"` lets each block pick its own direction, so Arabic and English paragraphs mix correctly.
const auto = <T extends keyof React.JSX.IntrinsicElements>(Tag: T) =>
  function Auto(props: ComponentProps<T>) {
    // biome-ignore lint/suspicious/noExplicitAny: generic intrinsic element passthrough
    const T2 = Tag as any;
    return <T2 dir="auto" {...props} />;
  };

const components = {
  p: auto("p"),
  li: auto("li"),
  h1: auto("h1"),
  h2: auto("h2"),
  h3: auto("h3"),
  h4: auto("h4"),
  blockquote: auto("blockquote"),
  pre: CodeBlock,
  img: (p: ComponentProps<"img">) => (
    // biome-ignore lint/a11y/useAltText: alt comes from the markdown source
    <img
      {...p}
      loading="lazy"
      className="my-2 max-h-[28rem] max-w-full rounded-xl border border-[var(--line)]"
    />
  ),
  a: (p: ComponentProps<"a">) => <a {...p} target="_blank" rel="noopener noreferrer nofollow" />,
  table: (p: ComponentProps<"table">) => (
    <div className="my-3 overflow-x-auto">
      <table {...p} />
    </div>
  ),
};

export const Markdown = memo(function Markdown({ text }: { text: string }) {
  return (
    <div className="prose-qn">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: false, ignoreMissing: true }]]}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});
